import React,{useEffect,useState} from "react";
import { DetailsBox, EmptyField, FieldShell } from "../components/FieldShell";
import LongTextValue from "./LongTextValue";
import { googleMapsEmbedSrc, googleMapsHref } from "../utils/mapUtils";
import {
  dropdownLabelFromRow,
  formatDisplayValue,
  getBaseField,
  isTrue,
} from "../utils/valueUtils";
import { isHttpUrl } from "../utils/fileUtils";

export function PrimitiveField({ label, column, raw }) {
  const [expanded, setExpanded] = useState(false);

  const value = formatDisplayValue(raw, column);
  const stringValue = String(value ?? "");

  const previewLines = Math.max(1, Number(column?.previewLines) || 3);

  /*
   * Collapse the previous value when the table moves
   * to another row or the field value changes.
   */
  useEffect(() => {
    setExpanded(false);
  }, [raw, column?.field]);

  if (stringValue.trim() === "" || stringValue === "—") {
    return (
      <FieldShell label={label} column={column}>
        <span className="safety-row-details__dash">—</span>
      </FieldShell>
    );
  }

  return (
    <FieldShell
      label={label}
      column={column}
      className={[
        "safety-row-details__field--long-text",
        expanded ? "safety-row-details__field--text-expanded" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <LongTextValue
        value={stringValue}
        lines={previewLines}
        expanded={expanded}
        onExpandedChange={setExpanded}
      />
    </FieldShell>
  );
}

export function BooleanField({ label, column, raw }) {
  return (
    <FieldShell label={label} column={column}>
      <span
        className={`safety-row-details__chip ${
          raw
            ? "safety-row-details__chip--success"
            : "safety-row-details__chip--muted"
        }`}
      >
        {raw ? "Yes" : "No"}
      </span>
    </FieldShell>
  );
}

export function DropdownField({ row, label, column }) {
  const labelText = dropdownLabelFromRow(row, column);
  const baseField = getBaseField(column.field);
  const selectedOption = row?.[baseField];

  return (
    <FieldShell
      label={label}
      column={column}
      details={
        isTrue(column.details) && selectedOption ? (
          <DetailsBox source={selectedOption} fields={column.BoxShow} />
        ) : null
      }
    >
      {labelText ? (
        <span className="safety-row-details__chip">{labelText}</span>
      ) : (
        <span className="safety-row-details__dash">—</span>
      )}
    </FieldShell>
  );
}

export function SignatureField({ label, column, raw }) {
  if (!isHttpUrl(raw)) {
    return <EmptyField label={label} column={column} />;
  }

  return (
    <FieldShell
      label={label}
      column={column}
      className="safety-row-details__field--images"
    >
      <div className="safety-row-details__signature-preview">
        <img
          className="safety-row-details__signature"
          src={raw}
          alt={`${label} signature`}
          onClick={() => window.open(raw, "_blank", "noopener,noreferrer")}
          onError={(event) => {
            event.currentTarget.replaceWith(
              Object.assign(document.createElement("span"), {
                className: "safety-row-details__dash",
                textContent: "—",
              }),
            );
          }}
        />

        <a
          href={raw}
          target="_blank"
          rel="noopener noreferrer"
          className="safety-row-details__link"
        >
          View Full Signature
        </a>
      </div>
    </FieldShell>
  );
}

export function GeolocationField({ label, column, raw }) {
  const embedSrc = googleMapsEmbedSrc(raw);
  const href = googleMapsHref(raw);

  return (
    <FieldShell
      label={label}
      column={column}
      className="safety-row-details__field--map"
    >
      {embedSrc ? (
        <>
          <div className="safety-row-details__map-embed">
            <iframe
              title="Map"
              src={embedSrc}
              width="100%"
              height="100%"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>

          {href ? (
            <a
              className="safety-row-details__link"
              href={href}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in Google Maps
            </a>
          ) : null}
        </>
      ) : (
        <span className="safety-row-details__dash">—</span>
      )}
    </FieldShell>
  );
}
